// Which text fills the street line after a Google suggestion is picked.
//
// Google has no premise record for some streets. Places Autocomplete then
// echoes the number the visitor typed onto a ROUTE-level prediction ("2014 Alto
// Cedro Drive, Beverly Hills…", types ["route", "geocode"], synthetic `Ei…` id),
// and Place Details for that id resolves to the street itself — `street` comes
// back "Alto Cedro Dr" with no street_number. Filling the line from details
// alone drops the number the visitor typed and saw in the dropdown (GSE-12408).
//
// In that one case the prediction's main text — the exact line rendered in the
// dropdown and clicked — is kept as the street. Everywhere else details win, so
// a number Google did supply ("1600 Amphitheatre Pkwy") is never replaced by the
// typed spelling, and an establishment pick never becomes the street.
//
// "Has a number" means a leading house-number TOKEN followed by a space —
// "1600 …", "12A …", "12-14 …" — not merely a leading digit. An ordinal street
// name ("10th St", "5th Ave") starts with a digit but carries no house number,
// and reading it as one re-loses the number on exactly the streets where this
// bug is most common.
//
// Order matters, and it is NOT "details with a number always win". A route-only
// result means Google supplied no street_number at all, so a leading number in
// `street` is part of the street's NAME — "8 Mile Rd", "102 Ave NW", "48 Ave".
// Treating that as a verified house number dropped the typed one again across
// Detroit's Mile Roads, the Edmonton/Calgary numbered grid and Queens' numbered
// avenues, which is the same population as the ordinal streets above. So the
// route-only question is asked first, and details win on every other shape.
//
// Not every country puts the number first. Spain, Germany, the Netherlands and
// Italy write "Calle Mayor 12", "Hauptstrasse 5" — a route-only result drops
// those numbers exactly the same way. A trailing number is only trusted when
// removing it leaves precisely the street Google resolved, which is what tells a
// house number ("Calle Mayor" + 12) apart from a street NAMED with a trailing
// number ("Route 66", where the remainder would be "Route").
//
// The prediction's `name` is NOT usable here: the autocomplete Lambda can return
// it truncated to the matched span alone ("6329" for "6329 Lantana Pines
// Circle"), so only `structured_formatting.main_text` carries the full line.
//
// Mirrors cruises-next `lib/domain/place-street.ts`, which fixes the same bug on
// the booking flow's own AddressInput, with two deliberate differences: items
// here are snake_cased by `camelToSnakeCase` (hence
// `structured_formatting.main_text`), and there is no `|| details.name` fallback
// — cruises-next's AddressInput has always had one, this hook never did, and
// adding it would let a business name ("Port Everglades") become the street line.

const PREMISE_TYPES = ['premise', 'street_address', 'subpremise']

/** Details describe a street, not a building on it. */
export const isRouteOnly = (types: readonly string[] | null | undefined): boolean =>
  !!types && types.includes('route') && !types.some((t) => PREMISE_TYPES.includes(t))

/** The fields of a selected autocomplete item this decision reads. */
export interface StreetPrediction {
  type?: string
  structured_formatting?: { main_text?: string | null } | null
}

/** The fields of a getPlaceDetails result this decision reads. */
export interface StreetDetails {
  street?: string | null
  types?: readonly string[] | null
}

const HOUSE_NUMBER = '\\d+[A-Za-z]?(-\\d+)?'
const LEADING_NUMBER = new RegExp(`^${HOUSE_NUMBER}\\s`)
const TRAILING_NUMBER = new RegExp(`\\s${HOUSE_NUMBER}$`)

const hasHouseNumber = (s: string): boolean => LEADING_NUMBER.test(s)

/** "Calle Mayor 12" -> "Calle Mayor". Null when there is no trailing number. */
const withoutTrailingNumber = (s: string): string | null => {
  const match = s.match(TRAILING_NUMBER)
  return match ? s.slice(0, s.length - match[0].length).trim() : null
}

/**
 * Resolves the street line for an address built from a picked suggestion.
 *
 * @param prediction  the autocomplete item the visitor clicked
 * @param details     the getPlaceDetails result for that item's place id
 * @returns the street line to store as `address_1`
 */
export const resolveStreetLine = (
  prediction: StreetPrediction | null | undefined,
  details: StreetDetails,
): string => {
  const fromDetails = details.street || ''
  if (prediction?.type !== 'GooglePlace') return fromDetails
  const typed = (prediction.structured_formatting?.main_text ?? '').trim()
  // Google placed the pick on a building: its number is verified, the typed one
  // is not. Only a route-only result is missing a number we can trust.
  if (!isRouteOnly(details.types)) return fromDetails
  if (hasHouseNumber(typed)) return typed
  // Number-last countries: trust it only if what remains IS the resolved street.
  const withoutNumber = withoutTrailingNumber(typed)
  if (withoutNumber && fromDetails && withoutNumber.toLowerCase() === fromDetails.toLowerCase()) {
    return typed
  }
  return fromDetails
}
